/**
 * The SC2 command-card model: the SC2Command / SC2Context / SC2Roster types, the mode + globals
 * constants, the serving-unit fallback, and the roster/context selector functions. The curated
 * command + roster DATA lives in sc2-commands.ts (re-exported here so the registry stays the public
 * face for consumers); the co-op and campaign data live in sc2-coop.ts / sc2-campaign.ts.
 */
import { SC2_COMMANDS, SC2_ROSTERS } from "./sc2-commands";

export { SC2_COMMANDS, SC2_ROSTERS };

/** One curated card entry: a Command plus where its binding lives in the file. */
export interface SC2Command {
  name: string;
  /** An optional cap abbreviation of `name`, set only where the full name overflows the keycap;
   * the bindings-list and readout show `name`. */
  short?: string;
  /** SC2_CATEGORIES id. */
  category: string;
  /** [Commands] id in the .SC2Hotkeys file, the part left of the "/". */
  key: string;
  /** The caster of a scoped button, the part right of the "/": the game reads `key/unit=` rows
   * for it and nothing else. Absent = a universal button read from the flat `key=` row only -
   * NOT a harmless default: per-unit rows on it are dead text (see servingUnit). */
  unit?: string;
  note?: string;
  /**
   * A build-menu opener: the submenu Context this command drills into when pressed
   * (Build Basic / Build Advanced). Pressing or clicking the opener navigates to `opens`; absent
   * for ordinary commands. The navigation reads `opens` from this registry, never the domain Binding.
   */
  opens?: string;
}

/**
 * Which unit bucket serves a command's binding, given the table's buckets for that command. The
 * game's binding space is a fixed id set: a button is either universal (one shared flat `Cmd=` row
 * for every unit that shows it - Move, Stop, Stim) or caster-scoped (its own `Cmd/Caster=` row -
 * EMP/Ghost, SupplyDepot/SCV); the other form is dead text the game never reads (in-game verified
 * 2026-07-07: a `Stop/Marine=` row binds nothing, in any line order, with or without a flat
 * sibling). A registry entry mirrors that: `unit` names the caster of a scoped button, unit-less
 * means universal. Serving is therefore an exact lookup through the entry's own form - the entry's
 * unit bucket when the table has it, else the flat `_def` bucket - never a different caster's
 * bucket (the old any-unit fallback painted phantom keys on every card sharing an entry). `_def`
 * also backstops a scoped entry because the vendored default tables encode every default flat
 * (GameHotkeys carries no caster part); a file's own flat row on a scoped button is kept serving
 * for the same reason - keylens cannot tell a family-default row from dead text without the
 * game's per-button universal flag, so it reads the file the way it reads its own defaults.
 * One rung sits above the per-form lookup: the Grid reconstruction's per-Context faces
 * (see the contextToken comment below). Both file-preferring rungs carry the fromFile guard so
 * a base-only reconstruction row never masks a rebind the file really made. The resolver picks
 * in token space (sc2-ingest servingValue, `fromFile` off the merged-under table) and
 * placedZoneCodes in code space (render-aux hotkeyFor, bare table, no `fromFile`), so the
 * ladder is defined once instead of mirrored in lockstep.
 */
export function servingUnit(
  byUnit: Record<string, unknown>,
  entryUnit: string | undefined,
  fromFile?: (unit: string) => boolean,
  contextToken?: string,
): string | undefined {
  // Grid is projected from a card position, so the same command grids differently on different
  // Contexts (a Barracks trains Marauder at a different cell in WoL vs co-op). The Grid table carries
  // a per-Context `key/<roster>/<ctx>` row for those; when this Context has one, it is the faithful
  // face and wins. No other preset writes such tokens, so this is inert for Standard/Classic/Lefties.
  // Guarded by fromFile like every file-preferring branch below: when the row is base-only (a Grid
  // UPLOAD flatly rebinds the ability, so its own file never carries the per-Context token), the
  // rebind must still win, so we fall through to the ladder rather than let the base row clobber it.
  if (contextToken && contextToken in byUnit && (!fromFile || fromFile(contextToken)))
    return contextToken;
  // same file-over-base guard as the rung above: the only base carrying qualified rows is the
  // reconstructed Grid (tier-2 `id/<unit>` faces), and a face must lose to a flat rebind the
  // file really made - the game reads the player's flat row, not our reconstruction.
  if (entryUnit && entryUnit in byUnit && (!fromFile || fromFile(entryUnit))) return entryUnit;
  if ("_def" in byUnit) return "_def";
  return undefined;
}

/**
 * The smart-verb cluster a selection's command card carries, sourced from Blizzard unit data
 * (a unit shows Move/Stop/Hold/Patrol if it can move, Attack if it has a weapon - decision 0033):
 * `basic` = Move+Stop+Hold+Patrol+Attack (a mobile combat unit), `flying` = those minus Attack (a
 * mover with no weapon: Overlord, Medivac, Raven, a caster), `tower` = Stop+Attack (static defence).
 * Absent = no verbs (a production building, a build submenu). Distinct from `cancel`.
 */
export type SC2Cluster = "basic" | "flying" | "tower";

/** A selectable unit or structure: picking it swaps the command card. */
export interface SC2Context {
  id: string;
  label: string;
  /** Dropdown section: a mobile `unit` vs a `building`/structure, from the
   * jcfieldsdev unit/building type. Absent on the leading "nothing" option (rendered ungrouped). */
  kind?: "unit" | "building";
  /** SC2_COMMANDS ids on this selection's command card. */
  card: string[];
  /**
   * The Blizzard smart-verb cluster this selection shows (decision 0033). The cluster's verbs render
   * as a per-Context underlay below the card, so a real card ability on a shared key still wins.
   * Absent = no movement/attack verbs (a building, a build submenu).
   */
  cluster?: SC2Cluster;
  /** Whether this selection's card carries Cancel (Blizzard membership). Buildings and build
   * submenus have it; most mobile units do not (it surfaces only with a queued/channelled order). */
  cancel?: boolean;
  /** Mode ids this context exists in; absent means all modes. */
  modes?: string[];
  /**
   * A build submenu: the worker Context this card drills down from. Present on
   * Build Basic / Build Advanced cards, which are reached by pressing their opener rather than
   * picked from the unit list, so contextsFor hides parent-bearing Contexts from the dropdown.
   */
  parent?: string;
}

/**
 * A selectable SC2 roster: the second command-card axis. In multiplayer/campaign it is a Race
 * (Terran / Zerg / Protoss); in co-op it is a Commander (Raynor, Kerrigan, ...). `race` carries the
 * underlying faction (commander rosters group under it in the picker); `modes` gates which modes
 * offer the roster (rostersFor). The hand-authored MP rosters and their merge with the generated
 * COOP_ROSTERS / campaign rosters live in sc2-commands.ts.
 */
export interface SC2Roster {
  label: string;
  race: "terran" | "zerg" | "protoss";
  modes: string[];
  contexts: SC2Context[];
}

export const SC2_MODES: { id: string; label: string }[] = [
  { id: "multiplayer", label: "Multiplayer" },
  { id: "campaign", label: "Campaign" },
  { id: "coop", label: "Co-op" },
];

// The six universal verbs. The live resolver no longer paints cards from this list - per-Context
// Blizzard clusters now own card membership - so it survives only as the reference set the
// registry-id and co-op parity guards check against. Kept as a named export for those tests; it is
// not a projection input.
export const SC2_GLOBALS = ["move", "attack", "stop", "hold", "patrol", "cancel"];

/**
 * The rosters offered by a mode: the second command-card selector's options (Race in MP/campaign,
 * Commander in co-op). `race` rides along so the co-op picker can group commanders by faction.
 */
export function rostersFor(mode: string): Array<{ id: string; label: string; race: string }> {
  return Object.entries(SC2_ROSTERS)
    .filter(([, r]) => r.modes.includes(mode))
    .map(([id, r]) => ({ id, label: r.label, race: r.race }));
}

/** The selected roster if the mode still offers it, else the mode's first roster. Mirrors
 * validContextId: switching mode keeps the raw selection but renders a valid one. */
export function validRosterId(mode: string, selected: string): string {
  const offered = rostersFor(mode);
  return offered.find((r) => r.id === selected)?.id ?? offered[0]?.id ?? "terran";
}

/**
 * The contexts a roster offers in the unit dropdown for a mode (an ungated context exists in all
 * modes). Build submenus (parent-bearing) are reached by drilling into their opener,
 * never picked from the list, so they are excluded here.
 */
export function contextsFor(roster: string, mode: string): SC2Context[] {
  const r = SC2_ROSTERS[roster];
  if (!r) return [];
  return r.contexts.filter((c) => !c.parent && (!c.modes || c.modes.includes(mode)));
}

/**
 * The build submenu a command drills into when pressed, or undefined for an ordinary
 * command. Drives drill-down: pressing or clicking an opener navigates the command card to `opens`.
 */
export function openerFor(cmd: string | undefined): string | undefined {
  return cmd ? SC2_COMMANDS[cmd]?.opens : undefined;
}

/**
 * The worker Context a build submenu drills back to (its parent), or undefined for a top-level
 * context. Drives back-out: Escape or the Cancel hotkey returns to the parent.
 */
export function parentContext(roster: string, contextId: string): string | undefined {
  return SC2_ROSTERS[roster]?.contexts.find((c) => c.id === contextId)?.parent;
}

/** The selected context if the roster and mode still offer it, else the first offered. */
export function validContextId(roster: string, mode: string, selected: string): string {
  const offered = contextsFor(roster, mode);
  const kept = offered.find((c) => c.id === selected);
  if (kept) return kept.id;
  // a drilled-in build submenu (parent-bearing, hidden from the dropdown) stays valid while its
  // parent unit is offered, so a deep selection survives a re-render
  const sub = SC2_ROSTERS[roster]?.contexts.find((c) => c.id === selected && c.parent);
  if (sub?.parent && offered.some((c) => c.id === sub.parent)) return sub.id;
  return offered[0]?.id ?? "none";
}
